import { ComponentConfig, usePuck } from '@measured/puck';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import { ReduxSelectorProps } from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  viewPorts,
} from '../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import { getIsEdit } from '../../utils/constants';
import AccordionButton from './customComponents/dropDown';
import { useEffect, useState } from 'react';
import OrderSummartDetais from './OrderContent';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import OrderSummaryContent from '../../puck/reusable-props/orderSummaryContent';
import OrderSummaryHeader from '../../puck/reusable-props/orderSummaryHeader';
import CartDetails from './CartDetails';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { Switch } from '@mui/material';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';

export type Props = {
  fontSize: string;
  fontWeight: string;
  showOnMobile: boolean;
  showOnTablet: boolean;
  showOnDesktop: boolean;
  textColor: string;
  showDivider?: boolean;
  placeholder?: string;
};

export type OrderSummaryContentPropsDefault = {
  Subtotal: Props;
  Shipping: Props;
  GrandTotal: Props;
  Discounts: Props;
  EstimatedAmount: Props;
  SalesTax: Props;
  CalculatedFees: Props;
  showOnDesktop: boolean;
  showOnTablet: boolean;
  showOnMobile: boolean;
  border: IBorderValueProps;
  backgroundColor: string;
  productDetails: IOrdersProductDetails;
} & MarginProps &
  PaddingProps;

export type IHeaderProps = {
  openText: string;
  closeText: string;
};

export type OrderHeaderProps = {
  HeaderText: {
    textColor: string;
    fontSize: string;
    fontWeight: string;
    openText: string;
    closeText: string;
  };
  showOnDesktop: boolean;
  showOnTablet: boolean;
  showOnMobile: boolean;
  backgroundColor: string;
} & MarginProps &
  PaddingProps;

export type backgroundColorProps = {
  backgroundColor: string;
};

export type IOrdersProductDetails = {
  productName: Props;
  productPrice: Props;
  productVarient: Props;
  productQuantity: Props;
  showOnDesktop: boolean;
  showOnTablet: boolean;
  showOnMobile: boolean;
} & MarginProps &
  PaddingProps;

export type OrderSummaryProps = {
  content: OrderSummaryContentPropsDefault;
  header: OrderHeaderProps;
  isDefaultOpen: boolean;
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
} & showComponentProps &
  MarginProps &
  PaddingProps;

export const OrderSummary = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<OrderSummaryProps> => {
  return {
    label: (
      <CustomTooltip title="OrderSummary">
        <span>Order Summary</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      header: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <>
            <OrderSummaryHeader
              value={value}
              onChange={onChange}
              title="Header"
              showHover={true}
            />
          </>
        ),
      },
      content: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <>
            <OrderSummaryContent
              value={value}
              onChange={onChange}
              title="Content"
              showHover={true}
            />
          </>
        ),
      },

      isDefaultOpen: {
        type: 'custom',
        label: 'Default Open',
        render: ({ value, onChange }) => (
          <div className="flex gap-2 items-center justify-between">
            <label>Default Collapsed</label>
            <Switch
              checked={value}
              onChange={e => onChange(e.target.checked)}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </div>
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      isDefaultOpen: false,
      ...borderProps,
      background: {
        color: '#000000',
        isInherit: false,
      },
      margin: {
        desktop: {
          top: '6',
          bottom: '6',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '6',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '4',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      content: {
        margin: {
          desktop: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
          tablet: {
            top: '0',
            bottom: '6',
            right: '0',
            left: '0',
          },
          mobile: {
            top: '0',
            bottom: '4',
            right: '0',
            left: '0',
          },
        },
        padding: {
          deviceType: 'desktop',
          paddingBottom: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingLeft: {
            desktop: '10',
            mobile: '0',
            tablet: '0',
          },
          paddingRight: {
            desktop: '30',
            mobile: '0',
            tablet: '0',
          },
          paddingTop: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
        },
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
        Subtotal: {
          placeholder: 'Subtotal',
          textColor: '#4B5563',
          fontSize: 'text-sm',
          fontWeight: 'font-normal',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          showDivider: false,
        },
        Shipping: {
          placeholder: 'Shipping',
          textColor: '#4B5563',
          fontSize: 'text-sm',
          fontWeight: 'font-normal',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          showDivider: false,
        },
        EstimatedAmount: {
          placeholder: 'Estimated taxes',
          textColor: '#4B5563',
          fontSize: 'text-sm',
          fontWeight: 'font-normal',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          showDivider: false,
        },
        SalesTax: {
          placeholder: 'Sales Tax',
          textColor: '#4B5563',
          fontSize: 'text-sm',
          fontWeight: 'font-normal',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          showDivider: false,
        },
        CalculatedFees: {
          textColor: '#4B5563',
          fontSize: 'text-sm',
          fontWeight: 'font-normal',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          showDivider: false,
        },
        GrandTotal: {
          placeholder: 'Grand Total',
          textColor: '#000000',
          fontSize: 'text-lg',
          fontWeight: 'font-semibold',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          showDivider: false,
        },
        Discounts: {
          placeholder: 'Discount Total',
          textColor: '#4B5563',
          fontSize: 'text-sm',
          fontWeight: 'font-normal',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          showDivider: false,
        },
        ...borderProps,
        backgroundColor: '#ffffff',
        productDetails: {
          margin: {
            desktop: {
              top: '0',
              bottom: '0',
              right: '0',
              left: '0',
            },
            tablet: {
              top: '0',
              bottom: '0',
              right: '0',
              left: '0',
            },
            mobile: {
              top: '0',
              bottom: '0',
              right: '0',
              left: '0',
            },
          },
          padding: {
            deviceType: 'desktop',
            paddingBottom: {
              desktop: '0',
              mobile: '0',
              tablet: '0',
            },
            paddingLeft: {
              desktop: '0',
              mobile: '0',
              tablet: '0',
            },
            paddingRight: {
              desktop: '0',
              mobile: '0',
              tablet: '0',
            },
            paddingTop: {
              desktop: '0',
              mobile: '0',
              tablet: '0',
            },
          },
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          productName: {
            textColor: '#4B5563',
            fontSize: 'text-sm',
            fontWeight: 'font-normal',
            showOnDesktop: true,
            showOnTablet: true,
            showOnMobile: true,
          },
          productPrice: {
            textColor: '#4B5563',
            fontSize: 'text-sm',
            fontWeight: 'font-normal',
            showOnDesktop: true,
            showOnTablet: true,
            showOnMobile: true,
          },
          productVarient: {
            textColor: '#4B5563',
            fontSize: 'text-sm',
            fontWeight: 'font-normal',
            showOnDesktop: true,
            showOnTablet: true,
            showOnMobile: true,
          },
          productQuantity: {
            textColor: '#4B5563',
            fontSize: 'text-sm',
            fontWeight: 'font-normal',
            showOnDesktop: true,
            showOnTablet: true,
            showOnMobile: true,
          },
        },
      },
      header: {
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
        margin: {
          desktop: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
          tablet: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
          mobile: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
        },
        padding: {
          deviceType: 'desktop',
          paddingBottom: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingLeft: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingRight: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingTop: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
        },
        backgroundColor: '#000000',
        HeaderText: {
          textColor: '#ffffff',
          fontSize: 'text-sm',
          fontWeight: 'font-normal',
          openText: 'Show Order Summary',
          closeText: 'Hide Order Summary',
        },
      },
    },
    render: ({
      content,
      header,
      showComponent,
      margin,
      padding,
      background,
      border,
      isDefaultOpen,
      editMode,
    }) => {
      const currentWidth = useAppSelector(state => state.store.currentWidth);
      let width = currentWidth;
      const isEditPage = getIsEdit();
      if (isEditPage) {
        const { appState } = usePuck();
        width = appState?.ui?.viewports?.current?.width;
      }
      const currentDeviceType = getcurrentDeviceType(currentWidth, editMode);
      const [isDropdownOpen, setIsDropdownOpen] = useState(!isDefaultOpen);

      const orderDetails = useAppSelector(
        state => state.orderDetails?.[0] || null,
      );
      const hideProductVariantId = useAppSelector(
        state => state.common.hideProductThankYou,
      );
      useEffect(() => {
        setIsDropdownOpen(!isDefaultOpen);
      }, [isDefaultOpen]);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const headerMarginStyles = getMarginStyles(
        header?.margin,
        currentDeviceType,
      );
      const contentMarginStyles = getMarginStyles(
        content?.margin,
        currentDeviceType,
      );
      return (
        <>
          {showComponent?.includes(currentDeviceType) &&
            (orderDetails || getIsEdit()) && (
              <div
                className={`${border?.borderStyle?.[currentDeviceType]}`}
                style={{
                  backgroundColor: background?.isInherit
                    ? 'inherit'
                    : background?.color,
                  ...getBorderProps(border, currentDeviceType),
                  ...marginStyles,
                  ...getPaddingStyles(padding, currentDeviceType),
                }}>
                {((header?.showOnMobile &&
                  width &&
                  width <= viewPorts.mobile) ||
                  (header?.showOnTablet &&
                    width &&
                    width > viewPorts.mobile &&
                    width <= viewPorts.tablet) ||
                  (header?.showOnDesktop &&
                    width &&
                    width > viewPorts.tablet)) && (
                  <div
                    className={`${(header?.HeaderText?.fontSize, header?.HeaderText?.fontWeight)}`}
                    style={{
                      color: header?.HeaderText?.textColor,
                      backgroundColor: header?.backgroundColor,
                      ...headerMarginStyles,
                      ...getPaddingStyles(header.padding, currentDeviceType),
                    }}>
                    <AccordionButton
                      isOpen={isDropdownOpen}
                      onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
                      fontSize={header?.HeaderText?.fontSize}
                      fontWeight={header?.HeaderText?.fontWeight}
                      showLabel={header?.HeaderText?.openText}
                      hideLabel={header?.HeaderText?.closeText}
                      color={header?.HeaderText?.textColor}
                    />
                  </div>
                )}

                {isDropdownOpen && (
                  <div
                    className={`
                   ${content?.border?.borderStyle?.[currentDeviceType]}`}
                    style={{
                      background: content?.backgroundColor,
                      ...getBorderProps(content.border, currentDeviceType),
                      ...contentMarginStyles,
                      ...getPaddingStyles(content.padding, currentDeviceType),
                    }}>
                    {(content?.productDetails?.showOnMobile &&
                      width &&
                      width <= viewPorts.mobile) ||
                    (content?.productDetails?.showOnTablet &&
                      width &&
                      width > viewPorts.mobile &&
                      width <= viewPorts.tablet) ||
                    (content?.productDetails?.showOnDesktop &&
                      width &&
                      width > viewPorts?.tablet) ? (
                      <CartDetails
                        value={orderDetails}
                        productDetails={content?.productDetails}
                        width={width}
                        hideProductThankYou={hideProductVariantId}
                      />
                    ) : null}

                    {(content?.showOnMobile &&
                      width &&
                      width <= viewPorts.mobile) ||
                    (content?.showOnTablet &&
                      width &&
                      width > viewPorts.mobile &&
                      width <= viewPorts.tablet) ||
                    (content?.showOnDesktop &&
                      width &&
                      width > viewPorts?.tablet) ? (
                      <OrderSummartDetais
                        {...content}
                        width={width}
                        isEditPage={isEditPage}
                        value={orderDetails}
                      />
                    ) : null}
                  </div>
                )}
              </div>
            )}
        </>
      );
    },
  };
};
